import { useState } from "react";
import Shell from "@/layout/Shell";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, X, Sparkles, BookOpen } from "lucide-react";

// Admin-curated knowledge base. The "Get instant help" AI on the new-ticket
// screen answers ONLY from these entries — good ones deflect real tickets.
export default function Knowledge() {
  const arts = useFetch(() => api.kb().then((r) => r?.articles || []), []);
  const blank = { id: null, title: "", problem: "", solution: "", keywords: "", active: true };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const editing = !!form.id;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim() || !form.solution.trim()) { toast.error("Title and solution are required"); return; }
    setBusy(true);
    try {
      if (editing) await api.updateKb(form.id, form); else await api.createKb(form);
      toast.success(editing ? "Article updated" : "Article added"); setForm(blank); arts.reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.deleteKb(id); toast.success("Deleted"); arts.reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <Shell role="admin" title="AI Knowledge Base" subtitle="Instant fixes the assistant offers before a ticket is raised">
      <div className="glass rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-1.5"><Sparkles className="h-4 w-4 text-violet-500" /><div className="overline">{editing ? "Edit article" : "Add an article"}</div></div>
        <p className="text-sm text-muted-foreground mb-4">When someone describes a problem on the new-ticket screen, the AI checks these first and suggests a fix. Write clear, step-by-step solutions to your most common issues.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={set("title")} placeholder="Reset your portal password" className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label>Keywords <span className="text-muted-foreground text-[11px]">(help matching)</span></Label><Input value={form.keywords} onChange={set("keywords")} placeholder="password, login, reset, locked" className="rounded-xl" /></div>
        </div>
        <div className="space-y-1.5 mt-3"><Label>Problem it solves <span className="text-muted-foreground text-[11px]">(optional)</span></Label><Input value={form.problem} onChange={set("problem")} placeholder="Can't log in / forgot password" className="rounded-xl" /></div>
        <div className="space-y-1.5 mt-3"><Label>Solution (steps)</Label><Textarea rows={4} value={form.solution} onChange={set("solution")} placeholder={"1. Go to Login → 'Forgot password'\n2. Enter your institute email\n3. Follow the reset link (check spam)"} className="rounded-xl" /></div>
        <div className="flex gap-2 mt-4">
          <Button disabled={busy} onClick={save} className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5">{editing ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{editing ? "Update" : "Add article"}</Button>
          {editing && <Button variant="outline" className="rounded-full gap-1.5" onClick={() => setForm(blank)}><X className="h-4 w-4" />Cancel</Button>}
        </div>
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><BookOpen className="h-4 w-4 text-muted-foreground" /><div className="overline">Articles</div></div>
        {arts.loading ? <p className="text-sm text-muted-foreground">Loading…</p>
          : !(arts.data || []).length ? <p className="text-sm text-muted-foreground">No articles yet. Until you add some, the AI simply drafts a ticket instead of offering a fix.</p>
          : (
            <div className="divide-y divide-border">
              {(arts.data || []).map((a) => (
                <div key={a.id} className="flex items-start justify-between py-3 gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{a.title} {a.active === false && <span className="text-[10px] text-muted-foreground">(hidden)</span>}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">{a.solution}</div>
                    {a.keywords && <div className="text-[10px] text-muted-foreground mt-0.5">🔑 {a.keywords}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setForm({ id: a.id, title: a.title, problem: a.problem || "", solution: a.solution, keywords: a.keywords || "", active: a.active !== false })}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </Shell>
  );
}
